import { CircleCheck, CircleX, Clock, Pause, TriangleAlert, type LucideIcon } from 'lucide-react'
import { EMPTY, money } from '@/lib/format'
import type { Tone } from '@/lib/tone'
import type { PaymentStatus, Subscription, SubscriptionStatus } from '@/pages/Companies/GetCompany/SubscriptionSettings/types'

export interface SubscriptionStatusConfig {
  label: string
  tone: Tone
  icon: LucideIcon
}

export const subscriptionStatusConfig: Record<SubscriptionStatus, SubscriptionStatusConfig> = {
  active: { label: 'Ativa', tone: 'success', icon: CircleCheck },
  trialing: { label: 'Em teste', tone: 'info', icon: Clock },
  past_due: { label: 'Pagamento atrasado', tone: 'warning', icon: TriangleAlert },
  unpaid: { label: 'Não paga', tone: 'danger', icon: TriangleAlert },
  canceled: { label: 'Cancelada', tone: 'danger', icon: CircleX },
  incomplete: { label: 'Pagamento pendente', tone: 'warning', icon: Clock },
  incomplete_expired: { label: 'Expirada', tone: 'danger', icon: CircleX },
  paused: { label: 'Pausada', tone: 'neutral', icon: Pause }
}

const NO_SUBSCRIPTION: SubscriptionStatusConfig = { label: 'Sem assinatura', tone: 'neutral', icon: Clock }

export const getStatusConfig = (status: SubscriptionStatus | undefined): SubscriptionStatusConfig =>
  (status && subscriptionStatusConfig[status]) || NO_SUBSCRIPTION

export const paymentStatusConfig: Record<PaymentStatus, { label: string; tone: Tone }> = {
  paid: { label: 'Pago', tone: 'success' },
  pending: { label: 'Pendente', tone: 'warning' },
  failed: { label: 'Falhou', tone: 'danger' },
  refunded: { label: 'Reembolsado', tone: 'neutral' },
  partially_refunded: { label: 'Reembolso parcial', tone: 'neutral' }
}

const ENDED_STATUSES: SubscriptionStatus[] = ['canceled', 'incomplete_expired']

export const isSubscriptionEnded = (status?: SubscriptionStatus | null): boolean => !!status && ENDED_STATUSES.includes(status)

export function formatStripeAmount(amount?: number | null, currency?: string | null): string {
  if (amount == null || !Number.isFinite(amount)) return EMPTY
  const code = (currency || 'brl').toLowerCase()
  if (code === 'brl') return money(amount / 100)
  return `${(amount / 100).toFixed(2)} ${code.toUpperCase()}`
}

const INTERVAL_LABEL: Record<string, string> = {
  day: 'Diária',
  week: 'Semanal',
  month: 'Mensal',
  year: 'Anual'
}

export const billingIntervalLabel = (interval?: string | null): string => (interval ? (INTERVAL_LABEL[interval] ?? interval) : EMPTY)

const LONG_DATE = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })

const longDate = (iso?: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : LONG_DATE.format(date)
}

type PeriodFields = Pick<Subscription, 'status' | 'currentPeriodEnd' | 'cancelAtPeriodEnd' | 'canceledAt' | 'endedAt'>

export function subscriptionPeriodLabel(subscription: PeriodFields): string {
  if (isSubscriptionEnded(subscription.status)) {
    const ended = longDate(subscription.endedAt ?? subscription.canceledAt)
    return ended ? `Encerrada em ${ended}` : 'Assinatura encerrada'
  }
  const periodEnd = longDate(subscription.currentPeriodEnd)
  if (!periodEnd) return EMPTY
  if (subscription.cancelAtPeriodEnd) return `Termina em ${periodEnd}`
  if (subscription.status === 'trialing') return `Teste gratuito até ${periodEnd}`
  if (subscription.status === 'paused') return 'Cobranças pausadas'
  return `Próxima cobrança em ${periodEnd}`
}
